import { SentryUtil } from "app/frontend/utils/sentry";
SentryUtil.init();
import type { PageModule } from "./types";

import { createInertiaApp } from "@inertiajs/react";

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import "../styles";

import { CssBaseline, ThemeProvider } from "@mui/material";
import AdminLayout from "@frontend/components/layouts/AdminLayout";
import Layout from "@frontend/components/layouts/Layout";
import POWERREADY_THEME from "app/frontend/theme";
import { ToastContainer } from "react-toastify";
import { logDev } from "../utils/logging";
import { ErrorBoundary } from "@sentry/react";
import ErrorBoundaryHandler from "app/frontend/utils/sentry/ErrorBoundaryHandler";
import { onBoundaryError } from "app/frontend/utils/sentry/onBoundaryError";
import {
  validateInertiaPage,
  registerChunkErrorReload,
  reportChunkFailure,
} from "app/frontend/utils/chunkErrors";

const pages = import.meta.glob<PageModule>("../pages/**/*.tsx");
logDev("application.tsx - pages -", pages);

// Safety net for chunks that fail outside the page loader below (e.g. React.lazy).
registerChunkErrorReload();

document.addEventListener("DOMContentLoaded", () => {
  createInertiaApp({
    // Set default page title
    // see https://inertia-rails.dev/guide/title-and-meta
    // title: (title) =>
    // title
    //     ? `${title} | Understanding Con Edison Rate Plans`
    //     : 'Understanding Con Edison Rate Plans',

    resolve: async (_pageName: string) => {
      logDev("application.tsx - createInertiaApp - page pageName -", _pageName);

      const pageName = _pageName.startsWith("pages/")
        ? `../${_pageName}`
        : `../pages/${_pageName}`;

      const pageModule =
        pages[`${pageName}.tsx`] || pages[`${pageName}/index.tsx`];
      logDev("application.tsx - createInertiaApp - page -", {
        pageModule,
        pageName,
        _pageName,
      });

      // Throws (missing page) or never resolves (chunk failure → reload).
      const page = await validateInertiaPage(pageModule, pageName);

      logDev(
        "application.tsx - createInertiaApp - getting layout for pageName -",
        pageName,
      );
      page.layout =
        page.layout ||
        ((pageName.includes("/admin/") || pageName.includes("/bd/")) &&
        !pageName.includes("SignIn") &&
        !pageName.includes("AdminPassword")
          ? AdminLayout
          : Layout);

      logDev("application.tsx - createInertiaApp - return page -", pageName);
      return page;
    },

    /**
     * React.StrictMode forces components to be rendered twice in development
     * https://stackoverflow.com/a/60619061/6410635
     */
    setup({ el, App, props }) {
      logDev("application.tsx - render App", {
        el,
        App,
        props: props.initialPage.props,
      });

      createRoot(el!).render(
        <StrictMode>
          <ToastContainer />
          <ThemeProvider theme={POWERREADY_THEME}>
            <CssBaseline />
            <ErrorBoundary
              onError={onBoundaryError}
              fallback={<ErrorBoundaryHandler />}
            >
              <App {...props} />
            </ErrorBoundary>
          </ThemeProvider>
        </StrictMode>,
      );
    },
  }).catch(reportChunkFailure);
});
